import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDown, ChevronRight, Save, Pencil, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { projectAPI } from '@/lib/api/projects'
import { getApiErrorMessage } from '@/lib/api/errors'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'

interface PropertiesEditorProps {
  activity: ActivityWithExchanges
  onUpdated?: () => void
}

interface FormState {
  name: string
  code: string
  location: string
  unit: string
  comment: string
  reference_product: string
}

function formStateFromActivity(a: ActivityWithExchanges): FormState {
  // `comment` is part of the API response but not in the declared
  // ActivityWithExchanges shape (yet) — read defensively without `any`.
  const maybeComment = (a as { comment?: string }).comment
  return {
    name: a.name,
    code: a.code,
    location: a.location || '',
    unit: a.unit,
    comment: maybeComment || '',
    reference_product: a.reference_product || '',
  }
}

export function PropertiesEditor({ activity, onUpdated }: PropertiesEditorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<FormState>(() => formStateFromActivity(activity))

  // Reset form when the user selects a different activity node.
  const lastIdRef = useRef(activity.id)
  useEffect(() => {
    if (lastIdRef.current !== activity.id) {
      lastIdRef.current = activity.id
      setFormData(formStateFromActivity(activity))
      setIsEditing(false)
    }
  }, [activity])

  const handleSave = async () => {
    setSaving(true)
    try {
      // PATCH (RFC 7396): only send fields the user actually edited. The
      // baseline is the activity object we hydrated the form from. This
      // avoids clearing fields whose canonical value never reached the
      // form (e.g. `comment` is shipped as `comment_preview` on list
      // responses, so formData.comment would be '' and a blanket PATCH
      // would wipe the stored comment).
      const baseline = formStateFromActivity(activity)
      const patch: {
        code?: string
        name?: string
        unit?: string
        location?: string
        comment?: string
        reference_product?: string
      } = {}
      if (formData.name !== baseline.name) patch.name = formData.name
      if (formData.code !== baseline.code) patch.code = formData.code
      if (formData.unit !== baseline.unit) patch.unit = formData.unit
      if (formData.location !== baseline.location) patch.location = formData.location || 'GLO'
      if (formData.comment !== baseline.comment) patch.comment = formData.comment
      if (formData.reference_product !== baseline.reference_product)
        patch.reference_product = formData.reference_product

      if (Object.keys(patch).length === 0) {
        toast.success('No changes')
        setIsEditing(false)
        return
      }

      await projectAPI.updateActivity(activity.id, patch)
      toast.success('Activity updated')
      setIsEditing(false)
      onUpdated?.()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update activity'))
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData(formStateFromActivity(activity))
    setIsEditing(false)
  }

  const metadata: Record<string, unknown> =
    (activity as { metadata?: Record<string, unknown> }).metadata || {}
  const metadataKeys = Object.keys(metadata).filter(
    (k) => metadata[k] != null && metadata[k] !== '',
  )

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 hover:bg-accent/50 transition-colors">
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          <span className="text-xs font-medium">Properties</span>
        </div>
        {!isEditing && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation()
              setIsOpen(true)
              setIsEditing(true)
            }}
          >
            <Pencil className="h-3 w-3" />
          </Button>
        )}
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-3 pb-3 space-y-3">
          <div className="space-y-2">
            <div>
              <Label htmlFor="name" className="text-xs">
                Name
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                disabled={!isEditing}
                className="h-8 text-xs mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="code" className="text-xs">
                  Code
                </Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
                  disabled={!isEditing}
                  className="h-8 text-xs mt-1 font-mono"
                />
              </div>
              <div>
                <Label htmlFor="location" className="text-xs">
                  Location
                </Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                  disabled={!isEditing}
                  className="h-8 text-xs mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="unit" className="text-xs">
                Unit
              </Label>
              <Input
                id="unit"
                value={formData.unit}
                onChange={(e) => setFormData((prev) => ({ ...prev, unit: e.target.value }))}
                disabled={!isEditing}
                className="h-8 text-xs mt-1"
              />
            </div>

            <div>
              <Label htmlFor="reference_product" className="text-xs">
                Reference product
              </Label>
              <Input
                id="reference_product"
                value={formData.reference_product}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, reference_product: e.target.value }))
                }
                disabled={!isEditing}
                className="h-8 text-xs mt-1"
                placeholder="—"
              />
            </div>

            <div>
              <Label htmlFor="comment" className="text-xs">
                Description / comment
              </Label>
              <Textarea
                id="comment"
                value={formData.comment}
                onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))}
                disabled={!isEditing}
                className="text-xs mt-1 min-h-[60px]"
                placeholder="Notes about this activity (purpose, source, assumptions…)"
              />
            </div>

            {metadataKeys.length > 0 && (
              <div>
                <Label className="text-xs">Metadata</Label>
                <div className="mt-1 rounded-md border border-border divide-y divide-border text-xs">
                  {metadataKeys.map((k) => (
                    <div key={k} className="flex items-start gap-2 px-2 py-1.5">
                      <span className="text-muted-foreground font-mono text-[10px] shrink-0 min-w-[110px]">
                        {k}
                      </span>
                      <span className="break-words">{String(metadata[k])}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Read-only — edit via MCP tools.
                </p>
              </div>
            )}
          </div>

          {isEditing && (
            <div className="flex items-center gap-2 pt-2">
              <Button
                size="sm"
                className="h-7 text-xs gap-1.5 flex-1"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Save className="h-3 w-3" />
                )}
                Save
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
